import {Component, NgZone, ViewChild} from '@angular/core';
import {Events, Nav, Platform} from 'ionic-angular';
import {Storage} from "@ionic/storage";
import {StatusBar} from '@awesome-cordova-plugins/status-bar/ngx';
import {SplashScreen} from '@awesome-cordova-plugins/splash-screen/ngx';
import {AppStateProvider} from "../providers/app-state/app-state";
import {ClaEvents, ClaStorage} from "../model/events";
import {ApiRestProvider, Credentials} from "../providers/api-rest/api-rest";
import {LoadingProvider} from "../providers/loading/loading";
import {ExamProvider} from "../providers/exam/exam";
import {TranslateService} from "@ngx-translate/core";
import {ModalProvider} from "../providers/modal/modal";
import {LogService} from "../providers/logging/log.service";

@Component({
  template: `<ion-nav #nav swipeBackEnabled="false"></ion-nav>`,
})
export class MyApp {

  idleState = 'Not started.';
  timedOut = false;
  lastPing?: Date = null;
  @ViewChild('nav') nav: Nav;
  constructor(public platform: Platform,
              public statusBar: StatusBar,
              public splashScreen: SplashScreen,
              public appState: AppStateProvider,
              public storage: Storage,
              public events: Events,
              public api: ApiRestProvider,
              private loading: LoadingProvider,
              private ngZone: NgZone,
              private examData: ExamProvider,
              private translate: TranslateService,
              private modal: ModalProvider,
              private logger: LogService) {
    // Make our scan view and report functions available headless
    this.platform.ready().then(async () => {
      this.logger.info('PLATFORM IS READY');
      // If its running headless in the server, we will bypass the app state
      if (document.URL.includes('?headless=true')) {
        this.splashScreen.hide();

        return;
      }

      // Event Listeners

      this.events.subscribe(ClaEvents.NAV_APP_ROOT, async (page,params = undefined) => {
        this.logger.info(`NAVAPPROOT`, [{'page': page}, {'params': params}, {'SourceContext': 'app.component.ts'}]);

        // Special case for login page to dismiss popups
        if (page == 'LoginPage') {
          this.loading.dismiss();
          this.modal.dismiss();
        }
        this.nav.setRoot(page, params);
      });

      document.addEventListener("pause", () => {
        this.onPause();
      }, false);

      document.addEventListener("resume", () => {
        this.onResume();
      }, false);

      if (this.appState.isIpad) {
        // Hack to always scroll keyboard
        window.addEventListener("keyboardDidShow", () => {
          document.activeElement.scrollIntoView(true);
        });
      }

      // Okay, so the platform is ready and our plugins are available.
      // Here you can do any higher level native things you might need.
      this.statusBar.styleDefault();

      // Go to login to show spinner
      await this.nav.setRoot('LoadingPage');

      this.splashScreen.hide();

      // Clear login if they have it
      await this.storage.remove(ClaStorage.APP_LOGIN_TOKEN);

      // Head to login page
      await this.appState.evaluateState();

      // Setup translations
      this.initTranslate();
    });
  }

  initTranslate() {
    this.translate.setDefaultLang('en');
    this.translate.use('en');
  }
  // Setup bindings to allow our functions to be called from console
  ngOnInit() {
    window["com"] = window["com"] || {};
    window["com"].synapse = window["com"].synapse || {};
    window["com"].synapse.openScanView = this.openScanView.bind(this);
    window["com"].synapse.openReport = this.openReport.bind(this);
    window["com"].synapse.simulateIdle = this.simulateIdle.bind(this);

    window["com"].synapse.oldConsoleLog = window.console.log;
    window.console.log = this.consoleLog.bind(this);
    window.console.debug = this.consoleDebug.bind(this);
    window.console.info = this.consoleInfo.bind(this);
    window.console.warn = this.consoleWarn.bind(this);
    window.console.error = this.consoleError.bind(this);
  }

  // Cleanup on destroy
  ngOnDestroy() {
    if(window["com"].synapse == undefined) return;

    window["com"].synapse.openScanView = null;
    window["com"].synapse.openReport = null;
    window["com"].synapse.simulateIdle = null;
  }

  public consoleLog(args: any) {
    this.logger.log(args, [{'SourceContext': 'console'}]);
  }

  public consoleDebug(args: any) {
    this.logger.debug(args, [{'SourceContext': 'console'}]);
  }

  public consoleInfo(args: any) {
    this.logger.info(args, [{'SourceContext': 'console'}]);
  }

  public consoleWarn(args: any) {
    this.logger.warn(args, [{'SourceContext': 'console'}]);
  }

  public consoleError(args: any) {
    this.logger.error(args, [{'SourceContext': 'console'}]);
  }

  // Public functions to enter into angular
  public openScanView(token: string, scanId: string, server: string) {
    this.ngZone.run(() => this._openScanView(token, scanId, server));
  }

  public openReport(token: string, examId: string, server: string) {
    this.ngZone.run(() => this._openReport(token, examId, server));
  }

  public simulateIdle() {
    this.ngZone.run(() => {
      this.onResume();
    });
  }

  // Private functions to actuall do the things
  private async _openScanView(token: string, scanId: string, server: string) {
    if (token && scanId) {
      // log the user in
      this.storage.set(ClaStorage.APP_SWIPE_WALKTHROUGH_DONE, 'true');
      localStorage.setItem(ClaStorage.APP_SWIPE_WALKTHROUGH_DONE, 'true');
      this.storage.set(ClaStorage.APP_LOGIN_TOKEN, token);

      // Set server
      this.api.currentServer = server;

      // Get the practice and doctor
      let prac = await this.api.getPractice().toPromise();
      let doc = await this.api.getDoctor().toPromise();

      // Assign to app state
      this.appState.activePractice = prac;
      this.appState.activeDoctor = doc;

      // Go to scanview page
      await this.nav.setRoot('ScanViewDetailPage', {scanId});

      console.log('Page done?')
    } else {
      console.log('NO TOKEN');
    }
  }

  private async _openReport(token: string, examId: string, server: string) {
    if (token && examId) {
      // log the user in
      this.storage.set(ClaStorage.APP_SWIPE_WALKTHROUGH_DONE, 'true');
      localStorage.setItem(ClaStorage.APP_SWIPE_WALKTHROUGH_DONE, 'true');
      this.storage.set(ClaStorage.APP_LOGIN_TOKEN, token);

      // Set server
      this.api.currentServer = server;

      // Get the practice and doctor
      let prac = await this.api.getPractice().toPromise();
      let doc = await this.api.getDoctor().toPromise();

      // Assign to app state
      this.appState.activePractice = prac;
      this.appState.activeDoctor = doc;

      // Determine the exam needed
      let exam = await this.api.getExam(examId).toPromise();
      let pageInfo = await this.examData.viewReportByExam(exam);

      // Go to report page
      await this.nav.setRoot(pageInfo.name, pageInfo.params);

      console.log('Page done?')
    } else {
      console.log('NO TOKEN');
    }
  }

  onResume() {
    this.events.publish(ClaEvents.APP_RESUME);
    //alert("APP WAS RESUMED. Page: " + page);
  }

  onPause() {
    this.events.publish(ClaEvents.APP_PAUSE);
  }
}




// WEBPACK FOOTER //
// ./src/app/app.component.ts